import React, { useState } from "react";
import { Grid, Box, Link, Typography, IconButton } from "@mui/material";
import RegularizationAccordion from "@/pages/attendance/component/Accordion";
import HistoryRegularizePng from "@/assets/images/attendance/HistoryRegularize.png";
import renderActions from "@/pages/attendance/component/RenderActions";
import DetailRow from "@/pages/attendance/component/DetailRow";
import DisplayDates from "@/pages/attendance/component/DisplayDates";
import { ArrowBackIosNewOutlinedIcon } from "@/icons";
import ViewRegularizationList from "@/pages/attendance/views/regularization/ViewRegularizationList/ViewRegularizationList";
import Loader from "@/components/loader";
import { CustomAlert } from "@/components";
import { useWithdrawRegularizationByDateMutation } from "@/api/services/attendance/regularization/EmployeeRegularization";
import { toast } from "@/components/toastify";

const HistoryRegularize = ({ regularizationRecords, refetchHistory, isLoadingHistory }) => {
  const [showViewList, setShowViewList] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [openWithdrawPopUp, setOpenWithdrawPopUp] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [withdrawRegularizationByDate] =
    useWithdrawRegularizationByDateMutation();

  const regRecords = regularizationRecords || [];
  const attendanceStartDate =
    regularizationRecords?.data?.lockStartDate ||
    regularizationRecords?.data?.repoLockStartDate;
  const attendanceEndDate =
    regularizationRecords?.data?.lockEndDate ||
    regularizationRecords?.data?.repoLockEndDate;

  const withdrawClose = () => {
    setOpenWithdrawPopUp(false);
    setSelectedItem(null);
  };

  const handleWithdrawClick = (item) => {
    setSelectedItem({
      empId: item.empId,
      regCode: item.regularizationCode,
    });
    setOpenWithdrawPopUp(true);
  };

  const handleWithdrawPopUpSubmit = async () => {
    if (!selectedItem?.empId || !selectedItem?.regCode) return;

    setStatusLoading(true);
    try {
      await withdrawRegularizationByDate({
        empId: selectedItem.empId,
        regCode: selectedItem.regCode,
      }).unwrap();

      toast("success", "Regularization withdrawn successfully");
      await refetchHistory?.();
      withdrawClose();
    } catch (error) {
      console.error("Withdraw regularization failed:", error);
      toast(
        "error",
        error?.data?.message || "Failed to withdraw regularization"
      );
    } finally {
      setStatusLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .split("/")
      .join("/");
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "REG":
        return "Regularization";
      case "WFH":
        return "Work From Home";
      case "OD":
        return "On Duty";
      default:
        return "";
    }
  };

  const renderDetails = (item) => {
  const workTypes = item.appliedRegularizations
    ?.map((reg) => getStatusLabel(reg?.regularizationStatus))
    .filter((type) => type && type.trim() !== "") // remove null or empty
    .join(", ");

    return (
      <Grid container spacing={2}>
        <DetailRow label="Work Type" value={workTypes} />
        <DetailRow label="Applied On" value={formatDate(item.appliedDate)} />
        <DetailRow label="No. of days" value={item.noOfDays} />
        <DetailRow
          label="Applied For"
          value={<DisplayDates dates={item.regularizationDates} />}
        />
      </Grid>
    );
  };

  const actions = (item) => {
    const startDate = attendanceStartDate ? new Date(attendanceStartDate) : null;
    const endDate = attendanceEndDate ? new Date(attendanceEndDate) : null;

    const isDateWithinRange = item?.appliedRegularizations?.some(
      ({ attendanceDate }) => {
        if (!startDate || !endDate || !attendanceDate) return false;
        const date = new Date(attendanceDate);
        return date >= startDate && date <= endDate;
      }
    );

    const canWithdraw =
      isDateWithinRange && item?.status !== "Withdraw";

    return [
      {
        label: "View",
        variant: "outlined",
        sx: {
          backgroundColor: (theme) =>
            theme.palette.BudgieThemeColor.Primary[1200],
          color: (theme) => theme.palette.BudgieThemeColor.BaseWhite,
          p: (theme) => theme.spacing(1, 2),
          m: (theme) => theme.spacing(0, 1),
          borderRadius: (theme) => theme.shape.s,
        },
        hoverSx: {
          backgroundColor: (theme) =>
            theme.palette.BudgieThemeColor.Primary[1200],
          color: (theme) => theme.palette.BudgieThemeColor.BaseWhite,
          borderColor: (theme) => theme.palette.BudgieThemeColor.Primary[600],
        },
        onclick: () => {
          setShowViewList(true);
          setSelectedRecord(item);
        },
      },
      ...(canWithdraw
        ? [
            {
              label: "Withdraw",
              variant: "contained",
              color: "warning",
              sx: {
                backgroundColor: (theme) =>
                  theme.palette.BudgieThemeColor.Warning[100],
                color: (theme) => theme.palette.BudgieThemeColor.Warning[600],
                borderColor: "warning.main",
                p: (theme) => theme.spacing(1, 2),
                m: (theme) => theme.spacing(0, 1),
                borderRadius: (theme) => theme.shape.s,
              },
              hoverSx: {
                backgroundColor: "warning.main",
                color: "warning.contrastText",
                borderColor: "warning.main",
              },
              onclick: () => {
                handleWithdrawClick(item);
              },
            },
          ]
        : []),
    ];
  };

  return (
    <>
      {isLoadingHistory ? (
        <Loader />
      ) : (
        <>
          {showViewList ? (
            <>
              <Typography variant="h4">
                <IconButton
                  component={Link}
                  to="/attendance/regularization"
                  color="primary"
                  onClick={() => setShowViewList(false)}
                >
                  <ArrowBackIosNewOutlinedIcon />
                </IconButton>
                Review Regularizations
              </Typography>
              {/* <Button
            variant="contained"
            color="primary"
            onClick={() => setShowViewList(false)}
          >
            Back
          </Button>
          <Typography variant="h5">Regularization withdrawn</Typography> */}
              <ViewRegularizationList setShowViewList={setShowViewList} selectedRecord={selectedRecord} refetchHistory={refetchHistory} />
            </>
          ) : (
            <Grid container>
              <Grid item lg={8}>
                <RegularizationAccordion
                  showChip={true}
                  items={regRecords}
                  renderDetails={renderDetails}
                  renderActions={(item) => renderActions(item, actions(item))}
                  detailsGridProps={{ xs: 12, sm: 9 }}
                  containerSpacing={(theme) => theme.spacing(2)}
                  actionsGridProps={{ xs: 12, sm: 3 }}
                  accordionProps={{ elevation: 1 }}
                  getTitle={(item) => `Applied on :${formatDate(item.appliedDate)}`}
                  showActions={true}
                  getChipLabel={(item) => item.status}
                  getChipStyle={(item) => {
                    switch (item.status) {
                      case "Approved":
                        return {
                          backgroundColor: "#ECFDF3",
                          color: "#1DA850",
                          borderColor: "#A9D9BD",
                        };
                      case "Partially Approved":
                        return {
                          backgroundColor: "#ECFDF3",
                          color: "#1DA850",
                          borderColor: "#A9D9BD",
                        };
                      case "Rejected":
                        return {
                          backgroundColor: "#FEF3F2",
                          color: "#EC2424",
                          borderColor: "#F9B4B4",
                        };
                      case "Withdraw":
                        return {
                          backgroundColor: "#FFFBEA",
                          color: "#D47F00",
                          borderColor: "#FFD88D",
                        };
                      default:
                        return {};
                    }
                  }}
                />
              </Grid>
              <Grid
                item
                lg={4}
                display={{ xs: "none", lg: "flex" }}
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <Box>
                  <img src={HistoryRegularizePng} alt="History Regularize" />
                </Box>
              </Grid>
            </Grid>
          )}
        </>
      )}

      <CustomAlert
        open={openWithdrawPopUp}
        title="Withdraw Request"
        message="Are you sure you want to withdraw this Regularize?"
        onCancel={withdrawClose}
        onSubmit={handleWithdrawPopUpSubmit}
        cancelText="Cancel"
        submitText="Submit"
        isSubmitting={statusLoading}
      />
    </>
  );
};

export default HistoryRegularize;
